import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";
import { toast } from "sonner";
import type { Permission } from "@/lib/permissions";
import { assertRowsAffected, toUserMessage } from "@/lib/supabase-errors";

export interface TeamMember {
  id: string;
  user_id: string;
  role: "owner" | "admin" | "member" | "accountant" | "read_only";
  status: string;
  custom_permissions: string[] | null;
  created_at: string;
  email: string | null;
  full_name: string | null;
  avatar_url: string | null;
}


export function useTeamMembers() {
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const workspaceId = currentWorkspace?.id;

  const membersQuery = useQuery({
    queryKey: ["team-members", workspaceId],
    queryFn: async (): Promise<TeamMember[]> => {
      if (!workspaceId) return [];
      // Type cast required until `supabase gen types` is re-run after migration
      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: Record<string, string>
      ) => Promise<{ data: TeamMember[] | null; error: { message: string } | null }>)(
        "get_workspace_members_with_profiles",
        { p_workspace_id: workspaceId }
      );
      if (error) throw new Error(error.message);
      return data ?? [];
    },
    enabled: !!workspaceId,
  });

  const inviteMember = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: "admin" | "member" }) => {
      if (!workspaceId) throw new Error("No workspace selected");
      const { data, error } = await supabase.functions.invoke("invite-member", {
        body: { workspace_id: workspaceId, email: email.toLowerCase().trim(), role },
      });
      if (error) {
        // Extract the business-logic error message from the response body when available
        const body = await (error as { context?: Response }).context
          ?.json()
          .catch(() => null);
        throw new Error(body?.error ?? error.message);
      }
      if (data?.error) throw new Error(data.error);
    },
    onSuccess: () => {
      // Invite creates a row in workspace_invites, not workspace_members,
      // so the pending-invites list is what needs to refresh. Also nudge
      // team-members in case an existing pending row was refreshed.
      queryClient.invalidateQueries({ queryKey: ["workspace-invites", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["team-members", workspaceId] });
      toast.success("Invite sent");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const removeMember = useMutation({
    mutationFn: async (memberId: string) => {
      const { data, error } = await supabase
        .from("workspace_members")
        .delete()
        .eq("id", memberId)
        .select("id");
      if (error) throw new Error(error.message);
      assertRowsAffected(data, "delete this member");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members", workspaceId] });
      toast.success("Member deleted");
    },
    onError: (err: Error) => toast.error(toUserMessage(err, "Failed to delete member")),
  });

  const disableMember = useMutation({
    mutationFn: async (memberId: string) => {
      const { data, error } = await supabase
        .from("workspace_members")
        .update({ status: "inactive" } as never)
        .eq("id", memberId)
        .select("id");
      if (error) throw new Error(error.message);
      assertRowsAffected(data, "disable this member");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members", workspaceId] });
      toast.success("Account disabled");
    },
    onError: (err: Error) => toast.error(toUserMessage(err, "Failed to disable member")),
  });

  const reactivateMember = useMutation({
    mutationFn: async (memberId: string) => {
      const { data, error } = await supabase
        .from("workspace_members")
        .update({ status: "active" } as never)
        .eq("id", memberId)
        .select("id");
      if (error) throw new Error(error.message);
      assertRowsAffected(data, "reactivate this member");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members", workspaceId] });
      toast.success("Account reactivated");
    },
    onError: (err: Error) => toast.error(toUserMessage(err, "Failed to reactivate member")),
  });

  const updatePermissions = useMutation({
    mutationFn: async ({ memberId, permissions }: { memberId: string; permissions: Permission[] }) => {
      const { data, error } = await supabase
        .from("workspace_members")
        // cast needed until types.ts is regenerated after migration
        .update({ custom_permissions: permissions } as never)
        .eq("id", memberId)
        .select("id");
      if (error) throw new Error(error.message);
      assertRowsAffected(data, "change this member's permissions");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members", workspaceId] });
      toast.success("Permissions updated");
    },
    onError: (err: Error) => toast.error(toUserMessage(err, "Failed to update permissions")),
  });

  const updateRole = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: "admin" | "member" }) => {
      const { data, error } = await supabase
        .from("workspace_members")
        .update({ role } as never)
        .eq("id", memberId)
        .select("id");
      if (error) throw new Error(error.message);
      assertRowsAffected(data, "change this member's role");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members", workspaceId] });
      toast.success("Role updated");
    },
    onError: (err: Error) => toast.error(toUserMessage(err, "Failed to update role")),
  });

  return {
    members: membersQuery.data ?? [],
    isLoading: membersQuery.isLoading,
    inviteMember,
    removeMember,
    disableMember,
    reactivateMember,
    updatePermissions,
    updateRole,
  };
}
